import { useFeatureFlags } from '@chronosphereio/core';
import { merge } from 'lodash';
import { useMemo } from 'react';
import { useTimeZone } from '@/components/context';
import { EventItem } from '@/core';
import { useDataQueryResult, useEventContext, useTimeRange } from '@/plugin-system';
import { HeatMapChart } from './HeatMapChart';
import { HeatMapChartExplorerMode } from './HeatMapChartExplorerMode';
import {
  DEFAULT_COLOR_OPTIONS,
  DEFAULT_LEGEND_OPTIONS,
  DEFAULT_Y_AXIS_OPTIONS,
  HeatMapPanelProps,
  processData,
} from './heat-map-model';

const EMPTY_EVENTS: EventItem[] = [];

/**
 * Panel displaying a heatmap visualization
 */
export function HeatMapPanelLoaded(props: HeatMapPanelProps) {
  const {
    width,
    height,
    color,
    legend,
    yAxis,
    statLines,
    customColorScale,
    onEventClick,
    panelKey,
    syncGroup,
    yAxisScale,
    trimRange,
  } = props;
  const { timeZone } = useTimeZone();
  const { queryResults } = useDataQueryResult({ dataTypeFilter: 'HeatMap' });
  const { setTimeRange } = useTimeRange();
  const { absoluteTimeRange } = useTimeRange();
  const eventCtx = useEventContext();
  const { enableHeatmapPanelEvents, enableHistogramExplorerDisplay } = useFeatureFlags();

  const data = useMemo(() => processData(queryResults, { absoluteTimeRange }), [queryResults, absoluteTimeRange]);

  const legendOptions = useMemo(() => {
    return merge({}, DEFAULT_LEGEND_OPTIONS, legend);
  }, [legend]);

  const colorOptions = useMemo(() => {
    return merge({}, DEFAULT_COLOR_OPTIONS, color);
  }, [color]);

  const yAxisOptions = useMemo(() => {
    return merge({}, DEFAULT_Y_AXIS_OPTIONS, yAxis);
  }, [yAxis]);

  const events = enableHeatmapPanelEvents && eventCtx?.events ? eventCtx.events : EMPTY_EVENTS;

  if (enableHistogramExplorerDisplay) {
    return (
      <HeatMapChartExplorerMode
        width={width}
        height={height}
        timeZone={timeZone}
        colorOptions={colorOptions}
        legendOptions={legendOptions}
        data={data}
        yAxis={yAxisOptions}
        onDataZoom={setTimeRange}
        customColorScale={customColorScale}
        events={events}
        onEventClick={onEventClick}
        panelKey={panelKey}
        syncGroup={syncGroup}
        statLines={statLines}
        scaleType={!yAxisScale || yAxisScale === 'Linear' ? 'value' : 'log'}
        trimRange={trimRange}
      />
    );
  }

  return (
    <HeatMapChart
      width={width}
      height={height}
      timeZone={timeZone}
      colorOptions={colorOptions}
      legendOptions={legendOptions}
      data={data}
      yAxis={yAxisOptions}
      onDataZoom={setTimeRange}
      customColorScale={customColorScale}
      events={events}
      onEventClick={onEventClick}
      panelKey={panelKey}
      syncGroup={syncGroup}
    />
  );
}
